<template>
  <div
    class="dropdown"
    ref="dropdownRef"
    v-show="showMenu"
    @click.stop
    @mouseenter="clearMenuCloseTimer"
    :style="{
      left: `${menuX}px`,
      top: `${menuY}px`,
      transform: `translate(${isRightAligned ? '-100%' : '0'}, ${isUpAligned ? '-100%' : '0'})`,
      zIndex: 9999,
    }"
  >
    <div class="dropdown-menu">
      <template v-for="menu in reactiveMenuItems" :key="menu.key">
        <template v-if="menu">
          <div v-if="menu.label === '---'" class="menu-separator" key="separator-{{ menu.key }}"></div>

          <div
            v-else-if="menu.children && menu.children.length"
            class="menu-item has-children"
            :data-key="menu.key"
            key="has-child-{{ menu.key }}"
            @mouseenter.stop="handleSubMenuMouseEnter(menu)"
             @mouseleave.stop="handleSubMenuMouseLeave(menu)" 
          >
            <div class="menu-item-trigger" @click.stop="toggleSubMenu(menu)" style="cursor: pointer">
              <div class="menu-icon-container">
                <component v-if="menu.icon" :is="menu.icon" style="font-size: 14px" />
              </div>
              <div class="menu-text-container">
                <span>{{ menu.label }}</span>
              </div>
              <RightOutlined class="submenu-arrow" />
            </div>
            <div class="submenu-container" v-if="menu.showSubMenu" @mouseenter="clearMenuCloseTimer">
              <SubMenu :menuItems="menu.children" @menu-click="handleSubMenuClick" />
            </div>
          </div>

          <div
            v-else
            class="menu-item"
            @click="handleMenuItemClick(menu.key)"
            :disabled="menu.disabled"
            key="normal-{{ menu.key }}"
            @mouseenter.stop="handleNormalMenuItemMouseEnter" 
            @mouseenter="clearMenuCloseTimer"
          >
            <div class="menu-icon-container" style="display: flex !important;">
              <component
                v-if="menu.icon"
                :is="menu.icon"
                style="display: inline-block !important; width: 14px; height: 14px; color: #333 !important;"
              />
            </div>
            <div class="menu-text-container">
              <span>{{ menu.label }}</span>
            </div>
            <div class="menu-triangle-placeholder"></div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from "vue";
import { RightOutlined } from "@ant-design/icons-vue";
import { defineAsyncComponent } from "vue";

export interface ContextMenuItem {
  label: string;
  key: string;
  icon?: any;
  disabled?: boolean;
  children?: ContextMenuItem[];
  showSubMenu?: boolean;
}

const SubMenu = defineAsyncComponent(() => import("./SubMenu.vue"));

const props = defineProps<{
  menuItems: ContextMenuItem[];
  triggerTarget?: string;
  clickTriggerTarget?: string;
}>();

const reactiveMenuItems = ref<ContextMenuItem[]>([]);

const initSubMenuStates = (items: ContextMenuItem[]) => {
  items.forEach((item) => {
    if (item.children && item.children.length) {
      item.showSubMenu = false;
      initSubMenuStates(item.children);
    }
  });
};

const deepCloneMenu = (items: ContextMenuItem[]): ContextMenuItem[] => {
  return items.map(item => {
    const clone: ContextMenuItem = { ...item, showSubMenu: false };
    if (item.children) clone.children = deepCloneMenu(item.children);
    return clone;
  });
};

watch(() => props.menuItems, (newItems) => {
  if (newItems) {
    const clonedItems = deepCloneMenu(newItems);
    initSubMenuStates(clonedItems);
    reactiveMenuItems.value = clonedItems;
  }
}, { immediate: true, deep: true });

const dropdownRef = ref<HTMLDivElement | null>(null);
const showMenu = ref(false);
let menuCloseTimer: NodeJS.Timeout | null = null;
const menuX = ref(0);
const menuY = ref(0);
const isRightAligned = ref(false);
const isUpAligned = ref(false);

const calculateMenuPosition = async () => {
  await nextTick();
  if (!showMenu.value || !dropdownRef.value) return;

  const menuEl = dropdownRef.value.querySelector(".dropdown-menu");
  if (!menuEl) return;

  const menuRect = menuEl.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  isRightAligned.value = menuX.value + menuRect.width > viewportWidth;
  isUpAligned.value = menuY.value + menuRect.height > viewportHeight;
};

watch(showMenu, (newVal) => {
  if (newVal) {
    calculateMenuPosition();
  } else {
    reactiveMenuItems.value.forEach((menu) => {
      if (menu) menu.showSubMenu = false;
    });
  }
});
// 新增：鼠标移到普通菜单项时，关闭所有子菜单
const handleNormalMenuItemMouseEnter = () => {
  clearMenuCloseTimer();
  // 递归关闭所有子菜单
  const closeAllSubMenus = (items: ContextMenuItem[]) => {
    items.forEach((menu) => {
      if (menu.children && menu.children.length) {
        menu.showSubMenu = false;
        closeAllSubMenus(menu.children); // 递归关闭子级子菜单
      }
    });
  };
  closeAllSubMenus(reactiveMenuItems.value);
};
const clearMenuCloseTimer = () => {
  if (menuCloseTimer) {
    clearTimeout(menuCloseTimer);
    menuCloseTimer = null;
  }
};

const handleSubMenuMouseLeave = (menu: ContextMenuItem) => {
  clearMenuCloseTimer();
  // 延迟关闭，避免鼠标快速移动时闪烁
  menuCloseTimer = setTimeout(() => {
    if (menu.children && menu.children.length) {
      menu.showSubMenu = false;
    }
  }, 150);
};

// 3. 新增：递归关闭所有非当前的子菜单
const closeAllSubMenusExcept = (excludeMenu: ContextMenuItem) => {
  const closeSubMenus = (items: ContextMenuItem[]) => {
    items.forEach((menu) => {
      if (menu !== excludeMenu && menu.children && menu.children.length) {
        menu.showSubMenu = false;
      }
      if (menu.children) closeSubMenus(menu.children);
    });
  };
  closeSubMenus(reactiveMenuItems.value);
};

// 2. 修改：进入子菜单时关闭其他子菜单（currentMenu是形参，无需定义）
const handleSubMenuMouseEnter = (currentMenu: ContextMenuItem) => {
  clearMenuCloseTimer();
  // 关闭所有非当前的子菜单
  closeAllSubMenusExcept(currentMenu);
  // 打开当前子菜单
  if (currentMenu.children && currentMenu.children.length) {
    currentMenu.showSubMenu = true;
  }
};

const toggleSubMenu = (menu: ContextMenuItem) => {
  if (menu.children && menu.children.length) {
    clearMenuCloseTimer();
    menu.showSubMenu = !menu.showSubMenu;
  }
};

const emit = defineEmits<{
  (e: "menu-click", menuKey: string): void;
  
}>();

const handleMenuItemClick = (key: string) => {
  emit("menu-click", key);
  showMenu.value = false;
};

const handleSubMenuClick = (key: string) => {
  emit("menu-click", key);
  showMenu.value = false;
};

const openMenu = (e: MouseEvent) => {
  clearMenuCloseTimer();
  menuX.value = e.clientX;
  menuY.value = e.clientY;
  showMenu.value = true;
  nextTick(() => calculateMenuPosition());
};

const handleContextMenu = (e: MouseEvent) => {
  if (props.triggerTarget) {
    const target = e.target as HTMLElement;
    if (!target.closest(props.triggerTarget)) return;
  }
  e.preventDefault();
  openMenu(e);
};

const handleClickTrigger = (e: MouseEvent) => {
  if (!props.clickTriggerTarget) return;
  
  const target = e.target as HTMLElement;
  if (!target.closest(props.clickTriggerTarget)) return;

  e.stopPropagation();
  showMenu.value = false;
  setTimeout(() => openMenu(e), 0);
};

const handleClickOutside = (e: MouseEvent) => {
  if (!showMenu.value) return;
  
  if (props.clickTriggerTarget) {
    const target = e.target as HTMLElement;
    if (target.closest(props.clickTriggerTarget)) return;
  }
  
  if (dropdownRef.value && dropdownRef.value.contains(e.target as Node)) return;
  showMenu.value = false;
};

const handleWindowResize = () => {
  if (showMenu.value) calculateMenuPosition();
};

onMounted(() => {
  document.addEventListener("contextmenu", handleContextMenu);
  document.addEventListener("click", handleClickTrigger);
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("resize", handleWindowResize);
});

onUnmounted(() => {
  document.removeEventListener("contextmenu", handleContextMenu);
  document.removeEventListener("click", handleClickTrigger);
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", handleWindowResize);
  clearMenuCloseTimer();
});
</script>

<style scoped>
.dropdown {
  position: fixed;
}

.dropdown-menu {
  min-width: 160px;
  max-width: 166px;
  padding: 4px 0;
  background-color: #fff;
  /* border-radius: 4px; */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
  background: linear-gradient(90deg, #ffffff 0%, #f3f3f3 26px,#ffffff 27px,#ffffff 100%);
  border: 1px solid #828282;
}

.menu-item {
  padding: 4px 4px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-start !important;
  gap: 8px;
  transition: all 0.2s;
  text-align: left !important;
  border: 1px solid transparent;
  box-sizing: border-box;
  z-index: 1;
  position: relative;
  background-clip: padding-box;

  line-height: 18px; /* 强制固定行高 */
  height: 26px; /* 强制固定菜单项高度，统一行距 */  
}

.menu-item:hover {
  background-color: #b5d7f3;
  box-shadow: inset 0 0 0 1px #0f7fd8;
}

.menu-item.has-children {
  position: relative;
  overflow: visible;
}

.menu-item.disabled:hover {
  background-color: #ffffff;
  border: 1px solid transparent;
  cursor: not-allowed;
}

.menu-item-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
}

.menu-icon-container {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.menu-icon-container :deep(*) {
  display: inline-block !important;
  visibility: visible !important;
  font-size: 14px !important;
  /* color: #333 !important; */
  width: 16px !important;
  height: 16px !important;
}

.submenu-container :deep(.menu-icon-container .anticon) {
  display: inline-block !important;
  color: #333 !important;
}

.menu-text-container {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 1px;
  white-space: nowrap;
}

.submenu-arrow {
  font-size: 12px;
  color: #666;
  width: 16px;
  flex-shrink: 0;
  text-align: center;
  margin-left: auto !important;
}

.menu-triangle-placeholder {
  width: 16px;
  flex-shrink: 0;
}

.menu-separator {
  height: 1px;
  margin: 4px 0;
  background-color: #bdbdbd;
  margin-left: 38px;
  margin-right: 4px;
}

.menu-item.disabled {
  color: #999;
  cursor: not-allowed;
  background-color: transparent !important;
}

.submenu-container {
  position: absolute;
  left: 100%;
  top: 0;
  z-index: 10000;
}
</style>